import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Search, ChevronRight } from 'lucide-react';
import { MasterTopbar } from '../shell/MasterTopbar';
import { useMasterWorkspaces, type MasterWorkspaceListItem } from '../hooks/useMasterData';
import { colorFromSlug, initialsOf, statusLabel, statusTone } from '../lib/workspace-format';

type Filter = 'todos' | 'ativos' | 'suspensos' | 'arquivados';

function matchesFilter(w: MasterWorkspaceListItem, f: Filter): boolean {
  switch (f) {
    case 'ativos': return w.status === 'ACTIVE';
    case 'suspensos': return w.status === 'SUSPENDED';
    case 'arquivados': return w.status === 'ARCHIVED';
    default: return true;
  }
}

export function MasterWorkspacesScreen() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigate = useNavigate() as any;
  const { data, isLoading, error } = useMasterWorkspaces();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('todos');

  const items = data?.items ?? [];

  const filtered = useMemo(() => {
    return items.filter((w) => {
      if (!matchesFilter(w, filter)) return false;
      if (!q) return true;
      const hay = `${w.name} ${w.slug} ${w.adminEmail ?? ''}`.toLowerCase();
      return hay.includes(q.toLowerCase());
    });
  }, [items, filter, q]);

  const counts = {
    todos: items.length,
    ativos: items.filter((w) => w.status === 'ACTIVE').length,
    suspensos: items.filter((w) => w.status === 'SUSPENDED').length,
    arquivados: items.filter((w) => w.status === 'ARCHIVED').length,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '0 18px 28px' }}>
      <MasterTopbar title="Workspaces" sub={`${items.length} entidades`} />

      {/* Busca */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          height: 42,
          padding: '4px 4px 4px 14px',
          background: 'var(--card-soft)',
          border: '1px solid var(--card-stroke)',
          borderRadius: 999,
        }}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar nome, slug ou e-mail do admin"
          style={{
            flex: 1,
            background: 'transparent',
            border: 0,
            outline: 'none',
            fontSize: 13,
            color: 'var(--fg)',
            fontFamily: 'inherit',
          }}
        />
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            background: 'var(--card)',
            color: 'var(--fg-muted)',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Search size={14} strokeWidth={1.7} />
        </span>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 6, overflow: 'auto', paddingBottom: 2 }}>
        {([['todos', 'Todos'], ['ativos', 'Ativos'], ['suspensos', 'Suspensos'], ['arquivados', 'Arquivados']] as const).map(
          ([id, label]) => {
            const active = filter === id;
            return (
              <button
                key={id}
                onClick={() => setFilter(id)}
                style={{
                  flex: '0 0 auto',
                  padding: '7px 13px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  background: active ? 'var(--lilac-deep)' : 'var(--card-soft)',
                  color: active ? '#fff' : 'var(--fg-muted)',
                  border: active ? '1px solid transparent' : '1px solid var(--card-stroke)',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                {label}{' '}
                <span style={{ opacity: 0.7, fontFamily: 'var(--font-mono)' }}>{counts[id]}</span>
              </button>
            );
          },
        )}
      </div>

      {isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{ height: 70, background: 'var(--card-soft)', borderRadius: 16 }} />
          ))}
        </div>
      )}

      {error && (
        <div
          style={{
            padding: 14,
            background: 'var(--block-soft)',
            color: 'var(--block)',
            borderRadius: 12,
            fontSize: 13,
          }}
        >
          {(error as Error).message}
        </div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <div style={{ padding: 16, color: 'var(--fg-dim)', fontSize: 13, textAlign: 'center' }}>
          Nenhum workspace neste filtro.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.map((w) => {
          const tone = statusTone(w.status);
          const label = statusLabel(w.status);
          const color = colorFromSlug(w.slug);
          return (
            <button
              key={w.id}
              onClick={() => navigate({ to: '/m/master/workspaces/$id', params: { id: w.id } })}
              style={{
                textAlign: 'left',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 12,
                background: 'var(--card)',
                border: '1px solid var(--card-stroke)',
                borderRadius: 18,
                opacity: w.status === 'SUSPENDED' ? 0.7 : 1,
                cursor: 'pointer',
                font: 'inherit',
                color: 'var(--fg)',
              }}
            >
              <span
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  background: color,
                  color: '#fff',
                  display: 'grid',
                  placeItems: 'center',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 13,
                  fontWeight: 600,
                  flex: '0 0 42px',
                }}
              >
                {initialsOf(w.name)}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {w.name}
                </div>
                <div
                  style={{
                    fontSize: 10.5,
                    color: 'var(--fg-muted)',
                    fontFamily: 'var(--font-mono)',
                    marginTop: 2,
                  }}
                >
                  {w.userCount} usuário{w.userCount !== 1 ? 's' : ''} · {w.adminEmail ?? 'sem admin'}
                </div>
                <div style={{ marginTop: 6 }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '3px 9px',
                      borderRadius: 999,
                      fontSize: 10.5,
                      fontFamily: 'var(--font-mono)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      background: `var(--${tone}-soft)`,
                      color: `var(--${tone})`,
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: `var(--${tone})`,
                      }}
                    />
                    {label}
                  </span>
                </div>
              </div>
              <ChevronRight size={16} style={{ color: 'var(--fg-dim)' }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
